import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { ImageInterface } from '../pages/Accounts/Register'
import {
    getConfigs,
    listContactTypes,
    updateConfigs,
} from '../services/configs'
import { GLOBAL_REQUEST_CONFIGS } from '../services/generic'
import { getFileInBase64 } from '../services/media'
import { getDateInIsoFormat, validateEmail, validateRuc } from '../utils/maks'
import { useLoading } from './loading_context'
import { useSnack } from './snack_context'
import { useTranslation } from 'react-i18next'
import { isLogged } from '../services/auth'

export enum SaleMethodEnum {
    Normal = 0,
    CreateAsPending = 1,
}

export enum BalanceTypeEnum {
    Weight = 0,
    Price = 1,
}

export enum SystemBranchEnum {
    NoBranch = 0,
    Branch = 1,
}

export enum PrintMethodEnum {
    Complete = 0,
    DataOnly = 1,
}

export enum UseElectronicNoteEnum {
    NotUse = 0,
    Use = 1,
    UseFromConfigs = 2,
}

export enum ContactTypeEnum {
    Email = 1,
    Phone = 2,
    Whatsapp = 3,
    Facebook = 4,
    Instagram = 5,
    Address = 6,
    City = 7,
}

export enum SearchTypeEnum {
    Normal = 0,
    Barcode = 1,
}

export enum SalePrintMethodEnum {
    Ticket = 0,
    TicketTXT = 1,
    CommonNote = 2,
    CommonNoteTXT = 3,
    DontPrint = 4,
}

export enum CashierPrintMethodEnum {
    Ticket = 0,
    TicketTxt = 1,
    DontPrint = 2,
}

export enum PaymentPrintMethodEnum {
    DontPrint = 0,
    A4 = 1,
    Ticket = 2,
}

export enum ShowIdEnum {
    DontShow = 0,
    InternalCode = 1,
    Barcode = 2,
}

export enum PaymentScreen {
    Normal = 0,
    Market = 1,
    Auto = 2,
}

export enum TicketType {
    Complete = 0,
    Reduced = 1,
}

export enum ProductTicketType {
    Barcode = 0,
    NoBarcode = 1,
}

export interface ContactTypeInfo {
    id: number
    name: string
}

export interface ContactInfo {
    id?: number
    value: string
    contactType: ContactTypeInfo
    contactTypeId?: number
}

export const PIX_KEY_TYPES = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP']

export enum ChangeInBuy {
    SalePrice = 0,
    GainPorc = 1,
}

export enum PromissoryNodeMode {
    Complete = 0,
    DontShow = 1,
    WithoutProducts = 2,
}

export enum CalculateGain {
    AveragePrice = 0,
    LastPrice = 1,
}

export interface PriceComission {
    id?: number
    name: string
    comission: number
    porcentGain: number
}

export interface ConfigsInfo {
    id: number
    timeZone: number
    timeZoneCountry: string
    isSummerTime: boolean
    useMasterPassword: boolean
    masterPassword: string
    companyName: string | null
    companyLogo: ImageInterface | null
    companySlogan: string | null
    companyDocument: string | null
    companyRegime: string
    companyEmissionDepartmentId: string
    companyEmissionDepartmentDescription: string
    companyEmissionCityId: string
    companyEmissionCityDescription: string
    companyEconomicActivityId: string
    companyEconomicActivityDescription: string
    companyEmissionHouseNumber: string
    companyEmissionDistrictId: string
    companyEmissionDistrictDescription: string
    useElectronicNote: number
    publicCertificateEletronicNote: string
    privateKeyEletronicNote: string
    secretCodeEletronicNote: string
    identifierEletronicNote: string
    dateEletronicNote: Date | null
    saleMethod: number
    printMethod: number
    balance: {
        initialDigit: number | null
        barcodeLength: number | null
        balanceLength: number | null
        finalDigit: number | null
        balanceType: number
    }
    systemBranch: number
    ticketFontSize: number
    searchType: number
    salePrintMethod: number
    salePrintMethodPending: number
    itemPrintOrderSale: number
    cashierPrintMethod: number
    paymentPrintMethod: number
    noteAfterSale: boolean
    noteAfterFinalizeSale: boolean
    showOnlyNormalPaymentType: boolean
    printPosition: string | null
    productBarcodePrintPosition: string | null
    notesQnt: number
    printHalfPage: boolean
    showIdOnNote: boolean
    showBarcodeOnNote: boolean
    showBranchStock: boolean
    showId: number
    hideWrittenTotal: boolean
    paymentScreen: number
    promissoryNoteMode: number
    useLogoInTicket: boolean
    flagGastoPendiente: boolean
    qntDecimals: number
    billAnonymousClient: boolean
    isPixActivated: boolean
    pixKeyType: string | null
    pixKey: string | null
    date?: string
    hour?: string
    ticketType: number
    showSecondaryCoinTotalsTicket: boolean
    productTicketType: number
    needToChoosePaymentMethod: boolean
    chooseCashier: boolean
    changeInBuy: number
    incrementIva: boolean
    discountCashierDefault: boolean
    keepCashierValues: boolean
    showBranchNameInNote: boolean
    paginationRowsNumber: number
    isCifPriceEditable: boolean
    language: string
    ignoreCharacterLimitNote: boolean
    webhookUrl: string | null
    webhookUsername: string | null
    webhookPassword: string | null
    showNcmOnNote: boolean
    allowDuplicatedCode: boolean
    useMasterPasswordWhenSellLowerThanUnitPrice: boolean
    useMasterPasswordWhenClientLimitExceeds: boolean
    gainCalculationType: number
    isInternalCodeIncremental: boolean
    masterPasswordMinPrice: string
    masterPasswordMinPriceAutoprice: string
    showSerialInDelivery: boolean
    showLoteInDelivery: boolean
    blockSellLowerThanSalePriceAutoprice: boolean
    ticketsQnt: number
    searchByLocalization: boolean
    matrixFont: string
    deliveryNotesQnt: number
    budgetNotesQnt: number
    includeChangeInCashier: boolean
    canTransferNegativeStock: boolean
    productLimitInSale: number
    layoutConfigs: string | null
    showWeightInSale: boolean
    removeFeesFromReceipt: boolean
    hidePriceListInSale: boolean
    showPriceListInTable: boolean
    emailAutovalue: string
    phoneAutovalue: string
    showStockOnSale: boolean
    showWeightOnNote: boolean
    showObservationOnNote: boolean
    controlProductsInPreSale: boolean
    showImageOnSale: boolean
    showContractOnBudget: boolean
    showUnitPriceOnNote: boolean
    showDiscountPercentageOnNote: boolean
    showObservationOnSaleList: boolean
    showImageOnNote: boolean
    allowCreditNoteForCommonNote: boolean
    observationCommonNote: string | null
    observationBudget: string | null
    showStockOnBuy: boolean
    validateIp: boolean
    validIps: string
    refundDeliveredProducts: boolean
    modifySalePriceInBuyScreen: boolean
    whatsappPhone: string
    whatsappConfirmationEnabled: boolean
    contacts: ContactInfo[]
    priceComission: PriceComission[]
}

export interface ConfigsError {
    companyName: string
    companyDocument: string
    companyRegime: string
    companyEmissionDepartmentId: string
    companyEmissionDepartmentDescription: string
    companyEmissionCityId: string
    companyEmissionCityDescription: string
    companyEconomicActivityId: string
    companyEconomicActivityDescription: string
    companyEmissionHouseNumber: string
    companyEmissionDistrictId: string
    companyEmissionDistrictDescription: string
    publicCertificateEletronicNote: string
    privateKeyEletronicNote: string
    secretCodeEletronicNote: string
    identifierEletronicNote: string
    dateEletronicNote: string
    ticketFontSize: string
    balance: {
        initialDigit: string
        barcodeLength: string
        balanceLength: string
        finalDigit: string
    }
    contacts: string[]
}

export interface ConfigsContextInterface {
    configs: ConfigsInfo
    setConfigs: Dispatch<SetStateAction<ConfigsInfo>>
    configsError: ConfigsError
    setConfigsError: Dispatch<SetStateAction<ConfigsError>>
    validate(): boolean
    handleUpdateConfigs(): Promise<void>
    contactTypes: ContactTypeInfo[]
    setContactTypes: Dispatch<SetStateAction<ContactTypeInfo[]>>
    base64Logo: string
    initializedLogo: boolean
}

export const contactInfoDefault: ContactInfo = {
    value: '',
    contactType: { id: 0, name: '' },
}

export const priceComissionDefault: PriceComission = {
    name: '',
    comission: 0,
    porcentGain: 0,
}

export const configsDefault: ConfigsInfo = {
    id: 0,
    timeZone: 0,
    timeZoneCountry: '',
    useMasterPassword: false,
    masterPassword: '',
    isSummerTime: false,
    saleMethod: SaleMethodEnum.Normal,
    printMethod: PrintMethodEnum.Complete,
    balance: {
        initialDigit: null,
        barcodeLength: null,
        balanceLength: null,
        finalDigit: null,
        balanceType: BalanceTypeEnum.Weight,
    },
    contacts: [{ ...contactInfoDefault }],
    systemBranch: SystemBranchEnum.NoBranch,
    companyName: '',
    companyLogo: null,
    companySlogan: '',
    companyDocument: '',
    companyRegime: '',
    companyEmissionDepartmentId: '',
    companyEmissionDepartmentDescription: '',
    companyEmissionCityId: '',
    companyEmissionCityDescription: '',
    companyEconomicActivityId: '',
    companyEconomicActivityDescription: '',
    companyEmissionHouseNumber: '',
    companyEmissionDistrictId: '',
    companyEmissionDistrictDescription: '',
    useElectronicNote: 0,
    publicCertificateEletronicNote: '',
    privateKeyEletronicNote: '',
    secretCodeEletronicNote: '',
    identifierEletronicNote: '',
    dateEletronicNote: null,
    ticketFontSize: 12,
    searchType: 0,
    salePrintMethod: 0,
    salePrintMethodPending: 0,
    itemPrintOrderSale: 0,
    cashierPrintMethod: 0,
    paymentPrintMethod: 0,
    noteAfterSale: true,
    noteAfterFinalizeSale: true,
    showOnlyNormalPaymentType: false,
    printPosition: null,
    productBarcodePrintPosition: null,
    notesQnt: 0,
    printHalfPage: false,
    showIdOnNote: false,
    showBarcodeOnNote: false,
    showBranchStock: false,
    hideWrittenTotal: false,
    showId: ShowIdEnum.DontShow,
    paymentScreen: PaymentScreen.Normal,
    promissoryNoteMode: PromissoryNodeMode.Complete,
    useLogoInTicket: false,
    flagGastoPendiente: false,
    qntDecimals: 2,
    billAnonymousClient: false,
    isPixActivated: false,
    pixKeyType: null,
    pixKey: null,
    ticketType: TicketType.Complete,
    showSecondaryCoinTotalsTicket: false,
    productTicketType: ProductTicketType.Barcode,
    needToChoosePaymentMethod: true,
    chooseCashier: false,
    changeInBuy: ChangeInBuy.SalePrice,
    incrementIva: false,
    discountCashierDefault: false,
    keepCashierValues: false,
    showBranchNameInNote: false,
    paginationRowsNumber: 10,
    isCifPriceEditable: false,
    language: 'es',
    ignoreCharacterLimitNote: false,
    webhookUrl: null,
    webhookUsername: null,
    webhookPassword: null,
    showNcmOnNote: false,
    allowDuplicatedCode: false,
    useMasterPasswordWhenSellLowerThanUnitPrice: false,
    useMasterPasswordWhenClientLimitExceeds: false,
    gainCalculationType: CalculateGain.AveragePrice,
    isInternalCodeIncremental: false,
    priceComission: [{ ...priceComissionDefault }],
    masterPasswordMinPrice: '',
    masterPasswordMinPriceAutoprice: '',
    showSerialInDelivery: false,
    showLoteInDelivery: false,
    blockSellLowerThanSalePriceAutoprice: false,
    ticketsQnt: 1,
    searchByLocalization: false,
    matrixFont: '',
    deliveryNotesQnt: 1,
    budgetNotesQnt: 2,
    includeChangeInCashier: false,
    canTransferNegativeStock: false,
    productLimitInSale: 9999,
    layoutConfigs: null,
    showWeightInSale: false,
    removeFeesFromReceipt: false,
    hidePriceListInSale: false,
    showPriceListInTable: false,
    emailAutovalue: '',
    phoneAutovalue: '',
    showStockOnSale: false,
    showWeightOnNote: false,
    showObservationOnNote: false,
    controlProductsInPreSale: false,
    showImageOnSale: false,
    showContractOnBudget: false,
    showUnitPriceOnNote: false,
    showDiscountPercentageOnNote: false,
    showObservationOnSaleList: false,
    showImageOnNote: false,
    allowCreditNoteForCommonNote: true,
    observationCommonNote: null,
    observationBudget: null,
    showStockOnBuy: false,
    validateIp: false,
    validIps: '',
    refundDeliveredProducts: false,
    modifySalePriceInBuyScreen: true,
    whatsappPhone: '',
    whatsappConfirmationEnabled: false,
}

const configsErrorDefault: ConfigsError = {
    companyName: '',
    companyDocument: '',
    balance: {
        initialDigit: '',
        barcodeLength: '',
        balanceLength: '',
        finalDigit: '',
    },
    companyRegime: '',
    companyEmissionDepartmentId: '',
    companyEmissionDepartmentDescription: '',
    companyEmissionCityId: '',
    companyEmissionCityDescription: '',
    companyEconomicActivityId: '',
    companyEconomicActivityDescription: '',
    companyEmissionHouseNumber: '',
    companyEmissionDistrictId: '',
    companyEmissionDistrictDescription: '',
    publicCertificateEletronicNote: '',
    privateKeyEletronicNote: '',
    secretCodeEletronicNote: '',
    identifierEletronicNote: '',
    dateEletronicNote: '',
    contacts: [],
    ticketFontSize: '',
}

const ConfigsContext = createContext<ConfigsContextInterface>({
    configs: configsDefault,
    setConfigs: () => {},
    configsError: configsErrorDefault,
    setConfigsError: () => {},
    validate: () => false,
    handleUpdateConfigs: () => Promise.resolve(),
    contactTypes: [],
    setContactTypes: () => {},
    base64Logo: '',
    initializedLogo: false,
})

export function useConfigs() {
    return useContext(ConfigsContext)
}

interface Props {
    children: ReactNode
    onChange(configs: ConfigsInfo): void
}

export function ConfigsContextProvider({ children, onChange }: Props) {
    const { i18n } = useTranslation()
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const [configs, setConfigs] = useState<ConfigsInfo>(configsDefault)
    const [contactTypes, setContactTypes] = useState<ContactTypeInfo[]>([])
    const [configsError, setConfigsError] =
        useState<ConfigsError>(configsErrorDefault)
    const [base64Logo, setBase64Logo] = useState('')
    const [initializedLogo, setInitializedLogo] = useState(false)

    useEffect(() => {
        GLOBAL_REQUEST_CONFIGS.rows = configs.paginationRowsNumber
        i18n.changeLanguage(configs.language)
        if (configs.companyLogo) storeLogoInBase64(configs.companyLogo.content)
        onChange(configs)
    }, [configs])

    const loadConfigs = useCallback(async () => {
        loading.show()
        try {
            const res = await getConfigs()
            setConfigs(res)
            if (res.companyLogo) storeLogoInBase64(res.companyLogo.content)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }, [])

    const storeLogoInBase64 = useCallback(async (pathname: string) => {
        try {
            const res = await getFileInBase64(pathname)
            setBase64Logo(res)
        } catch (error) {
        }
        setInitializedLogo(true)
    }, [])

    const validate = useCallback(() => {
        let isOk = true
        const newConfigsError = JSON.parse(JSON.stringify(configsErrorDefault))

        const filteredContacts = configs.contacts.filter(
            (c) => c.value && c.contactType.id > 0
        )

        if (!configs.companyName) {
            isOk = false
            newConfigsError.companyName = 'Campo obrigatório'
        }
        if (!configs.companyDocument) {
            isOk = false
            newConfigsError.companyDocument = 'Campo obrigatório'
        }
        if (configs.companyDocument && !validateRuc(configs.companyDocument)) {
            isOk = false
            newConfigsError.companyDocument = 'Documento inválido'
        }
        if (!configs.companyRegime) {
            isOk = false
            newConfigsError.companyRegime = 'Campo obrigatório'
        }
        if (!configs.companyEmissionDepartmentId) {
            isOk = false
            newConfigsError.companyEmissionDepartmentId = 'Campo obrigatório'
        }
        if (!configs.companyEmissionDepartmentDescription) {
            isOk = false
            newConfigsError.companyEmissionDepartmentDescription =
                'Campo obrigatório'
        }
        if (!configs.companyEmissionCityId) {
            isOk = false
            newConfigsError.companyEmissionCityId = 'Campo obrigatório'
        }
        if (!configs.companyEmissionCityDescription) {
            isOk = false
            newConfigsError.companyEmissionCityDescription = 'Campo obrigatório'
        }
        if (!configs.companyEconomicActivityId) {
            isOk = false
            newConfigsError.companyEconomicActivityId = 'Campo obrigatório'
        }
        if (!configs.companyEconomicActivityDescription) {
            isOk = false
            newConfigsError.companyEconomicActivityDescription =
                'Campo obrigatório'
        }
        if (!configs.companyEmissionHouseNumber) {
            isOk = false
            newConfigsError.companyEmissionHouseNumber = 'Campo obrigatório'
        }
        if (!configs.companyEmissionDistrictId) {
            isOk = false
            newConfigsError.companyEmissionDistrictId = 'Campo obrigatório'
        }
        if (!configs.companyEmissionDistrictDescription) {
            isOk = false
            newConfigsError.companyEmissionDistrictDescription =
                'Campo obrigatório'
        }
        if (typeof configs.balance.initialDigit !== 'number') {
            isOk = false
            newConfigsError.balance.initialDigit = 'Campo obrigatório'
        }
        if (typeof configs.balance.barcodeLength !== 'number') {
            isOk = false
            newConfigsError.balance.barcodeLength = 'Campo obrigatório'
        }
        if (typeof configs.balance.balanceLength !== 'number') {
            isOk = false
            newConfigsError.balance.balanceLength = 'Campo obrigatório'
        }
        if (typeof configs.balance.finalDigit !== 'number') {
            isOk = false
            newConfigsError.balance.finalDigit = 'Campo obrigatório'
        }

        if (
            !filteredContacts.find(
                (c) => c.contactType.id === ContactTypeEnum.Address
            )
        ) {
            isOk = false
            snack.error('Pelo menos um endereço deve ser inserido')
        } else if (
            !filteredContacts.find(
                (c) => c.contactType.id === ContactTypeEnum.Phone
            )
        ) {
            isOk = false
            snack.error('Pelo menos um telefone deve ser inserido')
        } else if (
            !filteredContacts.find(
                (c) => c.contactType.id === ContactTypeEnum.Email
            )
        ) {
            isOk = false
            snack.error('Pelo menos um email deve ser inserido')
        }

        for (let i = 0; i < filteredContacts.length; i++) {
            if (
                filteredContacts[i].contactType.id === ContactTypeEnum.Email &&
                !validateEmail(filteredContacts[i].value)
            ) {
                isOk = false
                newConfigsError.contacts[i] = 'Email inválido'
            }
            if (
                filteredContacts[i].contactType.id === ContactTypeEnum.Phone &&
                filteredContacts[i].value.length > 15
            ) {
                isOk = false
                newConfigsError.contacts[i] =
                    'O telefone não pode ter mais de 15 caracteres'
            }
        }

        setConfigsError({ ...newConfigsError })
        return isOk
    }, [configs])

    const handleUpdateConfigs = useCallback(async () => {
        loading.show()
        try {
            await updateConfigs({
                fuso_horario: configs.timeZone,
                fuso_horario_pais: configs.timeZoneCountry,
                flag_horario_verao: configs.isSummerTime ? 1 : 0,
                senha_mestre: configs.masterPassword
                    ? configs.masterPassword
                    : null,
                metodo_venda: configs.saleMethod,
                nome_empresa: configs.companyName,
                logo_empresa: configs.companyLogo?.isBase64
                    ? configs.companyLogo.content
                    : null,
                slogan_empresa: configs.companySlogan,
                documento_empresa: configs.companyDocument,
                digito_inicial_balanca: configs.balance.initialDigit ?? 0,
                qnt_digitos_cod_barra_balanca:
                    configs.balance.barcodeLength ?? 0,
                qnt_digitos_balanca: configs.balance.balanceLength ?? 0,
                digito_final_balanca: configs.balance.finalDigit ?? 0,
                tipo_balanca: configs.balance.balanceType,
                contatos: configs.contacts
                    .filter((c) => c.value && c.contactType.id > 0)
                    .map((c) => ({
                        valor: c.value,
                        id_tipo_contato: c.contactType.id,
                    })),
                flag_usar_senha_mestre: Number(configs.useMasterPassword),
                regime_empresa: configs.companyRegime,
                cod_departamento_emissao_empresa:
                    configs.companyEmissionDepartmentId,
                desc_departamento_emissao_empresa:
                    configs.companyEmissionDepartmentDescription,
                cod_cidade_emissao_empresa: configs.companyEmissionCityId,
                desc_cidade_emissao_empresa:
                    configs.companyEmissionCityDescription,
                cod_atividade_economica_empresa:
                    configs.companyEconomicActivityId,
                desc_atividade_economica_empresa:
                    configs.companyEconomicActivityDescription,
                num_casa_emissao_empresa: configs.companyEmissionHouseNumber,
                cod_distrito_emissao_empresa: configs.companyEmissionDistrictId,
                desc_distrito_emissao_empresa:
                    configs.companyEmissionDistrictDescription,
                flag_fatura_eletronica: configs.useElectronicNote,
                certificado_publico_fatura:
                    configs.publicCertificateEletronicNote
                        ? configs.publicCertificateEletronicNote
                        : null,
                chave_privada_fatura: configs.privateKeyEletronicNote
                    ? configs.privateKeyEletronicNote
                    : null,
                codigo_secreto_fatura: configs.secretCodeEletronicNote
                    ? configs.secretCodeEletronicNote
                    : null,
                codigo_identificador_fatura: configs.identifierEletronicNote
                    ? configs.identifierEletronicNote
                    : null,
                data_fatura: configs.dateEletronicNote
                    ? getDateInIsoFormat(configs.dateEletronicNote, true)
                    : null,
                flag_metodo_impressao: configs.printMethod,
                tamanho_fonte_ticket: configs.ticketFontSize,
                flag_tipo_buscador: configs.searchType,
                flag_impressao_venda: configs.salePrintMethod,
                flag_impressao_venda_pendente: configs.salePrintMethodPending,
                ordem_itens_impressao_venda: configs.itemPrintOrderSale,
                flag_impressao_caixa: configs.cashierPrintMethod,
                flag_impressao_pagamento: configs.paymentPrintMethod,
                flag_fatura_apos_venda: Number(configs.noteAfterSale),
                flag_fatura_apos_finalizar_venda: Number(
                    configs.noteAfterFinalizeSale
                ),
                flag_exibir_contado: Number(configs.showOnlyNormalPaymentType),
                posicoes_fatura_impressa: configs.printPosition,
                posicoes_etiqueta_produto: configs.productBarcodePrintPosition,
                quantidade_notas_fatura: configs.notesQnt,
                flag_imprimir_meia_folha: Number(configs.printHalfPage),
                flag_exibir_codigo_fatura: Number(configs.showIdOnNote),
                flag_exibir_codigo_barras_fatura: Number(
                    configs.showBarcodeOnNote
                ),
                flag_exibir_estoque_filiais: Number(configs.showBranchStock),
                flag_exibir_codigo: configs.showId,
                flag_esconder_total_letras: Number(configs.hideWrittenTotal),
                flag_tela_pagamento: configs.paymentScreen,
                flag_esconder_pagare: configs.promissoryNoteMode,
                flag_logo_ticket: Number(configs.useLogoInTicket),
                flag_gasto_pendiente: Number(configs.flagGastoPendiente),
                decimais_quantidade: configs.qntDecimals,
                flag_faturacao_cliente_anonimo: Number(
                    configs.billAnonymousClient
                ),
                flag_pix_habilitado: Number(configs.isPixActivated),
                tipo_chave_pix: configs.pixKeyType,
                chave_pix: configs.pixKey,
                flag_formato_ticket: configs.ticketType,
                flag_exibir_moeda_secundaria_ticket: Number(
                    configs.showSecondaryCoinTotalsTicket
                ),
                flag_formato_ticket_produto: configs.productTicketType,
                flag_escolher_metodo_pagamento: Number(
                    configs.needToChoosePaymentMethod
                ),
                flag_escolher_caixa: Number(configs.chooseCashier),
                flag_alterar_compra: configs.changeInBuy,
                flag_acrescentar_iva: Number(configs.incrementIva),
                flag_descontar_caixa_padrao: Number(
                    configs.discountCashierDefault
                ),
                flag_manter_valores_caixa: Number(configs.keepCashierValues),
                flag_exibir_nome_filial_nota: Number(
                    configs.showBranchNameInNote
                ),
                numero_linhas_paginacao: configs.paginationRowsNumber,
                flag_preco_cif_editavel: Number(configs.isCifPriceEditable),
                linguagem: configs.language,
                flag_ignorar_limite_caracteres_nota: Number(
                    configs.ignoreCharacterLimitNote
                ),
                webhook_url: configs.webhookUrl,
                webhook_username: configs.webhookUsername,
                webhook_password: configs.webhookPassword,
                flag_exibir_ncm_fatura: Number(configs.showNcmOnNote),
                flag_permitir_codigo_duplicado: Number(
                    configs.allowDuplicatedCode
                ),
                flag_usar_senha_mestre_vender_abaixo_preco_unitario: Number(
                    configs.useMasterPasswordWhenSellLowerThanUnitPrice
                ),
                flag_usar_senha_mestre_limite_excedido: Number(
                    configs.useMasterPasswordWhenClientLimitExceeds
                ),
                flag_tipo_calculo_lucro: configs.gainCalculationType,
                flag_codigo_interno_incremental: Number(
                    configs.isInternalCodeIncremental
                ),
                comissoesPreco: configs.priceComission
                    .filter((priceComission) => priceComission.name)
                    .map((priceComission) => ({
                        nome: priceComission.name,
                        comissao: priceComission.comission / 100,
                        porc_lucro: priceComission.porcentGain,
                    })),
                preco_minimo_usar_senha_mestre: configs.masterPasswordMinPrice,
                preco_minimo_usar_senha_mestre_autopreco:
                    configs.masterPasswordMinPriceAutoprice,
                flag_exibir_serial_entrega: Number(
                    configs.showSerialInDelivery
                ),
                flag_exibir_lote_entrega: Number(configs.showLoteInDelivery),
                flag_bloquear_autopreco_abaixo_preco_venda: Number(
                    configs.blockSellLowerThanSalePriceAutoprice
                ),
                quantidade_tickets: configs.ticketsQnt,
                flag_busca_localizacao: Number(configs.searchByLocalization),
                fonte_matricial: configs.matrixFont,
                quantidade_notas_entrega: configs.deliveryNotesQnt,
                quantidade_notas_orcamento: configs.budgetNotesQnt,
                flag_troco_caixa: Number(configs.includeChangeInCashier),
                flag_transferir_estoque_negativo: Number(
                    configs.canTransferNegativeStock
                ),
                limite_produtos_venda: configs.productLimitInSale,
                cores_sidebar: configs.layoutConfigs,
                flag_exibir_peso_venda: Number(configs.showWeightInSale),
                flag_remover_juros_recibo: Number(
                    configs.removeFeesFromReceipt
                ),
                flag_esconder_lista_precos_venda: Number(
                    configs.hidePriceListInSale
                ),
                flag_exibir_lista_precos_tabela: Number(
                    configs.showPriceListInTable
                ),
                email_autovalor: configs.emailAutovalue,
                telefone_autovalor: configs.phoneAutovalue,
                flag_exibir_stock_venda: Number(configs.showStockOnSale),
                flag_exibir_peso_fatura: Number(configs.showWeightOnNote),
                flag_exibir_observacao_fatura: Number(
                    configs.showObservationOnNote
                ),
                flag_controle_produto_pre_venda: Number(
                    configs.controlProductsInPreSale
                ),
                flag_exibir_imagem_venda: Number(configs.showImageOnSale),
                flag_exibir_pagare_orcamento: Number(
                    configs.showContractOnBudget
                ),
                flag_exibir_preco_unitario_fatura: Number(
                    configs.showUnitPriceOnNote
                ),
                flag_exibir_porcentagem_desconto_fatura: Number(
                    configs.showDiscountPercentageOnNote
                ),
                flag_exibir_observacao_lista_venda: Number(
                    configs.showObservationOnSaleList
                ),
                flag_exibir_imagem_fatura: Number(configs.showImageOnNote),
                flag_permitir_nota_credito_nota_comum: Number(
                    configs.allowCreditNoteForCommonNote
                ),
                observacao_nota_comum: configs.observationCommonNote || null,
                observacao_orcamento: configs.observationBudget || null,
                flag_exibir_stock_compra: Number(configs.showStockOnBuy),
                flag_validar_ip: Number(configs.validateIp),
                ips_validos: configs.validIps || null,
                flag_devolver_produtos_entregues: Number(configs.refundDeliveredProducts),
                flag_modificar_preco_venda_tela_compra: Number(configs.modifySalePriceInBuyScreen),
                telefone_whatsapp: configs.whatsappPhone || null,
                flag_confirmacao_whatsapp: Number(
                    configs.whatsappConfirmationEnabled
                ),
            })
            loadConfigs()
            snack.success('Configurações atualizadas com sucesso!')
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }, [configs, loadConfigs])

    const value = useMemo(
        () => ({
            configs,
            setConfigs,
            configsError,
            setConfigsError,
            validate,
            handleUpdateConfigs,
            contactTypes,
            setContactTypes,
            base64Logo,
            initializedLogo,
        }),
        [
            configs,
            setConfigs,
            configsError,
            setConfigsError,
            validate,
            handleUpdateConfigs,
            contactTypes,
            setContactTypes,
            base64Logo,
            initializedLogo,
        ]
    )

    return (
        <ConfigsContext.Provider value={value}>
            {children}
        </ConfigsContext.Provider>
    )
}
